import React, { useState, FormEvent } from 'react';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../components/SnookerBall';
import OAuthButtons from '../components/OAuthButtons';
import PasswordInput from '../components/PasswordInput';

interface SignupPageProps {
  onSignIn: () => void;
  onSignedUp: (email: string) => void;
  onPrivacy?: () => void;
}

export default function SignupPage({ onSignIn, onSignedUp, onPrivacy }: SignupPageProps) {
  const { signup } = useAuth();
  const { t } = useLang();
  const [username, setUsername] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [marketing, setMarketing] = useState(false);

  const passwordStrength = (): { label: string; color: string; width: string } => {
    if (!password) return { label: '', color: 'transparent', width: '0%' };
    const s = (password.length >= 12 ? 1 : 0) + (/[A-Z]/.test(password) ? 1 : 0) + (/[0-9]/.test(password) ? 1 : 0) + (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
    if (s <= 1) return { label: t.passwordWeak,   color: '#e74c3c', width: '25%' };
    if (s <= 2) return { label: t.passwordFair,   color: '#e67e22', width: '50%' };
    if (s <= 3) return { label: t.passwordGood,   color: '#f1c40f', width: '75%' };
    return          { label: t.passwordStrong, color: '#2ecc71', width: '100%' };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError(t.passwordsNoMatch); return; }
    if (password.length < 8)  { setError(t.passwordMinLength); return; }
    setLoading(true);
    try {
      await signup(username.trim(), email.trim(), password, marketing);
      onSignedUp(email.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const str = passwordStrength();

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <SnookerBallSpinner size={32} />
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', lineHeight: 1 }}>Shootout Comps</h1>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '0.3rem' }}>Create your free account</p>
        </div>

        <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.35)', borderRadius: '14px', padding: 'clamp(1.5rem,5vw,2.25rem)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            {/* Username */}
            <div>
              <label style={labelStyle}>{t.username}</label>
              <input value={username} onChange={e => setUsername(e.target.value)} placeholder="yourname" autoComplete="username" required style={inputStyle} />
              <p style={hintStyle}>{t.usernameHint}</p>
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" required style={inputStyle} />
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>{t.password}</label>
              <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" autoComplete="new-password" required style={inputStyle} />
              {password && (
                <>
                  <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: str.width, background: str.color, borderRadius: '2px', transition: 'all 0.3s' }} />
                  </div>
                  <p style={{ ...hintStyle, color: str.color, marginTop: '3px' }}>{str.label}</p>
                </>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label style={labelStyle}>{t.repeatPassword}</label>
              <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" autoComplete="new-password" required style={{ ...inputStyle, borderColor: confirm && confirm !== password ? '#e74c3c' : confirm && confirm === password ? '#2ecc71' : 'rgba(201,168,76,0.3)' }} />
            </div>

            {error && (
              <div style={{ background: 'rgba(192,57,43,0.18)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '6px', padding: '0.6rem 0.85rem', color: '#e74c3c', fontSize: '0.82rem' }}>
                {error}
              </div>
            )}

            {/* Marketing consent */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={marketing}
                onChange={e => setMarketing(e.target.checked)}
                style={{ marginTop: '2px', accentColor: 'var(--gold)', flexShrink: 0, width: '15px', height: '15px' }}
              />
              <span style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', lineHeight: 1.5 }}>
                I'd like to receive occasional emails about Shootout Comps news, features and tournaments. You can unsubscribe at any time.
              </span>
            </label>

            <button type="submit" disabled={loading || !username || !email || !password || !confirm} style={{
              marginTop: '0.25rem',
              background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
              color: 'var(--baize-dark)', border: 'none', borderRadius: '8px',
              padding: '0.9rem', fontSize: '0.95rem', fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
              opacity: loading || !username || !email || !password || !confirm ? 0.5 : 1,
              minHeight: '48px',
            }}>
              {loading ? t.creatingAccount : t.createAccount}
            </button>

            <p style={{ textAlign: 'center', color: 'var(--chalk-dim)', fontSize: '0.75rem', margin: 0 }}>
              We'll send a verification email to confirm your address. By creating an account you agree to our{' '}
              <a href="#privacy" onClick={e => { e.preventDefault(); onPrivacy ? onPrivacy() : window.open('/privacy', '_blank'); }} style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline', padding: 0 }}>
                {t.privacyPolicy}
              </a>.
            </p>
          </form>

          <div style={{ marginTop: '1.25rem' }}>
            <OAuthButtons />
          </div>
        </div>

        <p style={{ textAlign: 'center', color: 'var(--chalk-dim)', fontSize: '0.82rem', marginTop: '1.25rem' }}>
          Already have an account?{' '}
          <button onClick={onSignIn} style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', textDecoration: 'underline' }}>
            {t.signIn}
          </button>
        </p>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = { fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '0.32rem' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '0.72rem 0.9rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: 'var(--chalk)', outline: 'none', boxSizing: 'border-box' };
const hintStyle:  React.CSSProperties = { fontSize: '0.7rem', color: 'var(--chalk-dim)', margin: '3px 0 0', lineHeight: 1.4 };
